import { motion } from "framer-motion"
import { Icon } from "@iconify/react"
import { differenceInDays } from "date-fns"

import { Button } from "@/components/ui/button"
import { TypographySmall, TypographyXSmallMuted } from "@/components/ui/typography"
import { usePaymentContext } from "@/context-state/PaymentContext"
import { togglePricingPlansDialog } from '@/hooks/usePricingPlans'
import { cn } from "@/lib/utils"

// One shared upgrade banner under the module icons. Two states:
//   - Active trial → countdown ("Trial ends in N days") + Upgrade / Let's talk.
//   - Free plan    → "Free plan" + Upgrade.
// Paid tiers show nothing.
export const SidebarBanner = ({ className }) => {

    const { entitlements, isFreeTier } = usePaymentContext()

    if (!entitlements) {
        return null
    }

    const activeTrial = entitlements.activeTrial

    // Nothing to nudge for paying accounts.
    if (!activeTrial && !isFreeTier) {
        return null
    }

    let daysLeft = null
    if (activeTrial && entitlements.trialEndsAt) {
        const raw = differenceInDays(new Date(entitlements.trialEndsAt), new Date())
        daysLeft = raw < 1 ? 1 : raw
    }

    const title = activeTrial
        ? `Trial ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`
        : 'Free plan'
    const subtitle = activeTrial
        ? 'Upgrade before it ends'
        : 'Upgrade for unlimited access'

    return (
        <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={cn('rounded-lg border border-primary/20 bg-primary/[0.06] p-3', className)}
        >
            <div className="flex flex-col gap-2.5">
                <div className="flex items-start gap-2">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/15">
                        <Icon icon={activeTrial ? 'solar:clock-circle-bold' : 'solar:crown-bold'} className="h-3.5 w-3.5 text-primary" />
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <TypographySmall className="font-medium leading-tight">{title}</TypographySmall>
                        <TypographyXSmallMuted>{subtitle}</TypographyXSmallMuted>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        size="sm"
                        className="h-8 flex-1"
                        onClick={() => togglePricingPlansDialog(true)}
                    >
                        Upgrade
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => window.open('https://calendar.notion.so/meet/rdahan/iq2y63up0', '_blank')}
                    >
                        Let's talk
                    </Button>
                </div>
            </div>
        </motion.div>
    )
}
